import formActions from 'redux-form/lib/actions'
import {put, take, takeLeading} from 'redux-saga/effects'

import history from '../history'

import {
  LOGIN_FAILURE,
  LOGIN_SUCCESS,
  PASSWORD_RESET_REQUEST_FAILURE,
  PASSWORD_RESET_REQUEST_SUCCESS,
  REGISTRATION_FAILURE,
  REGISTRATION_SUCCESS,
  login,
  passwordResetRequest,
  registration,
} from '../actions/api'
import {
  LOGIN_VIA_LOGIN_FORM,
  REGISTER_VIA_REGISTRATION_FORM,
  RESET_PASSWORD_REQUEST_VIA_PASSWORD_RESET_REQUEST_FORM,
  registrationFinished,
} from '../actions/user'
import {
  HIDE_PASSWORD_RESET_REQUEST_FORM,
  SHOW_PASSWORD_RESET_REQUEST_SUCCESS,
} from '../actions/userInteractions'
import {REDUX} from '../const'
import * as track from '../track/track'
import * as URLS from '../urls'
import {getUrlParam, isValidNextParam} from '../utils'

export const CONTEXT = {
  SCHEDULER: 'scheduler',
  STATIC: 'static',
}

const location = (context) => {
  switch (context) {
    case CONTEXT.SCHEDULER:
      return 'Appointment Scheduler'
    case CONTEXT.STATIC:
      return 'Static'
    default:
      return 'Unknown'
  }
}

const onSuccess = (context, {email, isRegistration, nextRoute}) => {
  // nextParam can either be an input or a redirect
  const nextParam = nextRoute || getUrlParam('next')
  switch (context) {
    // peform analytics and any redirects
    case CONTEXT.SCHEDULER:
      // analytics
      if (isRegistration) {
        track.schedulerRegistered(email)
      } else {
        track.schedulerLoggedIn(email)
      }

      // follow a nextRoute argument on the action payload
      if (nextRoute) history.push(nextRoute)
      break
    case CONTEXT.STATIC:
      // TODO: GROWTH-927 if the following block seems silly, it is. This is
      //  how analytics work now... but will be fixed on the named ticket!
      // analytics
      if (isRegistration) {
        track.schedulerRegistered(email)
      } else {
        track.schedulerLoggedIn(email)
      }

      // check for a `next` param and follow that, otherwise go to the homepage
      if (isValidNextParam(nextParam)) {
        history.replace(nextParam)
      } else {
        window.location.href = URLS.HOME
      }

      break
    default:
      break
  }
}

// Login
// ----------------------------------------------------------------------------

export function* loginFormWorker({payload: {context, email, nextRoute, password}}) {
  try {
    // add the location to the action payload and dispatch
    yield put(login(email, location(context), password))

    // wait for a failure or success
    const {type, payload} = yield take([LOGIN_FAILURE, LOGIN_SUCCESS])

    if (type === LOGIN_SUCCESS) {
      onSuccess(context, {
        email,
        isRegistration: false,
        nextRoute,
      })
    } else {
      yield put(formActions.stopSubmit(REDUX.LOGIN_FORM, payload))
    }
  } catch (e) {
    console.error(`Could not complete ${location(context)} Login. Reason: ${e.message}`)
    yield put(
      formActions.stopSubmit(REDUX.LOGIN_FORM, {
        email: 'An unexpected error occurred. Please try again or refresh the page.',
      }),
    )
  }
}

export function* loginFormSaga() {
  yield takeLeading(LOGIN_VIA_LOGIN_FORM, loginFormWorker)
}

// Registration
// ----------------------------------------------------------------------------

export function* registrationFormWorker({payload: {context, email, nextRoute, password}}) {
  try {
    // add the location to the action payload and dispatch
    yield put(
      registration({
        email,
        location: location(context),
        password,
      }),
    )

    // wait for a failure or success
    const {type, payload} = yield take([REGISTRATION_FAILURE, REGISTRATION_SUCCESS])

    if (type === REGISTRATION_SUCCESS) {
      onSuccess(context, {
        email,
        isRegistration: true,
        nextRoute,
      })

      // this will get the user's user and event information and let the UI
      //  know that we're authenticated now
      yield put(registrationFinished())
    } else {
      yield put(formActions.stopSubmit(REDUX.REGISTRATION_FORM, payload))
    }
  } catch (e) {
    console.error(`Could not complete ${location(context)} Registration. Reason: ${e.message}`)
    yield put(
      formActions.stopSubmit(REDUX.REGISTRATION_FORM, {
        email: 'An unexpected error occurred. Please try again or refresh the page.',
      }),
    )
  }
}

export function* registrationFormSaga() {
  yield takeLeading(REGISTER_VIA_REGISTRATION_FORM, registrationFormWorker)
}

// Password Reset Request
// ----------------------------------------------------------------------------

export function* passwordResetRequestFormWorker({payload: {context, email}}) {
  try {
    // add the location to the action payload and dispatch
    yield put(passwordResetRequest(email, location(context)))
    // TODO: GROWTH-927 probably don't pass location here but use it for
    //  analytics elsewhere, like in this saga.

    // wait for a failure or success
    const {type, payload} = yield take([
      PASSWORD_RESET_REQUEST_FAILURE,
      PASSWORD_RESET_REQUEST_SUCCESS,
    ])

    if (type === PASSWORD_RESET_REQUEST_SUCCESS) {
      yield put({type: HIDE_PASSWORD_RESET_REQUEST_FORM})
      yield put({type: SHOW_PASSWORD_RESET_REQUEST_SUCCESS})
    } else {
      yield put(formActions.stopSubmit(REDUX.PASSWORD_RESET_REQUEST_FORM, payload))
    }
  } catch (e) {
    console.error(`Could not complete ${location(context)} Password Reset. Reason: ${e.message}`)
    yield put(
      formActions.stopSubmit(REDUX.PASSWORD_RESET_REQUEST_FORM, {
        email: 'An unexpected error occurred. Please try again or refresh the page.',
      }),
    )
  }
}

export function* passwordResetRequestFormSaga() {
  yield takeLeading(
    RESET_PASSWORD_REQUEST_VIA_PASSWORD_RESET_REQUEST_FORM,
    passwordResetRequestFormWorker,
  )
}
